import { getToolName, isToolUIPart } from "ai";
import {
  CopilotDataPartEnum,
  CopilotPartEnum,
  CopilotRoleEnum,
  CopilotToolEnum,
  CopilotToolStateEnum,
} from "~/utils/enums/copilot/chat.enums";
import { copilotResourceCards } from "~/utils/helpers/copilot/resources";
import { normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import { CopilotErrorCodeEnum } from "~/utils/enums/copilot/errors.enums";
import type { ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

const VISIBLE_DATA_PART_TYPES = new Set(Object.values(CopilotDataPartEnum).map((partType) => `data-${partType}`));
const ACTIONS_PART_TYPE = `data-${CopilotDataPartEnum.ACTIONS}`;
const ANALYSIS_PART_TYPE = `data-${CopilotDataPartEnum.ANALYSIS}`;
const ERROR_PART_TYPE = `data-${CopilotDataPartEnum.ERROR}`;
const MISSING_PART_TYPE = `data-${CopilotDataPartEnum.MISSING}`;
const RECAP_PART_TYPE = `data-${CopilotDataPartEnum.RECAP}`;
const RESOURCES_PART_TYPE = `data-${CopilotDataPartEnum.RESOURCES}`;
const VISIBLE_ACTION_TOOLS = new Set<CopilotToolEnum>([
  CopilotToolEnum.CREATE_TRAINING,
  CopilotToolEnum.UPDATE_TRAINING,
  CopilotToolEnum.ADD_PROVIDER,
  CopilotToolEnum.CREATE_SESSION,
]);
const HTML_TAG_REGEX = /<\/?[a-z][^>]*>/gi;
const HTML_WHITESPACE_ENTITY_REGEX = /&(nbsp|#160|#x0*a0);/gi;

function hasVisibleText(text: string): boolean {
  return !!text.replace(HTML_TAG_REGEX, "").replace(HTML_WHITESPACE_ENTITY_REGEX, " ").trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasVisibleIntroOrItems(data: unknown): boolean {
  if (!isRecord(data)) return false;

  return (
    (typeof data.intro === "string" && hasVisibleText(data.intro)) ||
    (Array.isArray(data.items) && data.items.length > 0)
  );
}

/** A recap paints its intro, its fields, its sections and its outro: with none of them, nothing. */
function hasVisibleRecap(data: unknown): boolean {
  if (!isRecord(data)) return false;

  return (
    (typeof data.intro === "string" && hasVisibleText(data.intro)) ||
    (typeof data.outro === "string" && hasVisibleText(data.outro)) ||
    (Array.isArray(data.fields) && data.fields.length > 0) ||
    (Array.isArray(data.sections) && data.sections.length > 0)
  );
}

/** Without a title or a single step, the steps block is a progress bar over nothing. */
function hasVisibleAnalysis(data: unknown): boolean {
  if (!isRecord(data)) return false;

  return (
    (typeof data.title === "string" && hasVisibleText(data.title)) ||
    (Array.isArray(data.steps) && data.steps.length > 0)
  );
}

function isVisibleDataPart(partType: string, data: unknown): boolean {
  switch (partType) {
    case RESOURCES_PART_TYPE:
      return !!copilotResourceCards(data).length;
    case ACTIONS_PART_TYPE:
    case MISSING_PART_TYPE:
      return hasVisibleIntroOrItems(data);
    case RECAP_PART_TYPE:
      return hasVisibleRecap(data);
    case ANALYSIS_PART_TYPE:
      return hasVisibleAnalysis(data);
    default:
      // A `data-error` always paints its message, normalized from whatever the stream carried.
      return true;
  }
}

/** Mirrors what `CopilotBlocks` can actually paint, rather than what merely exists in the stream. */
export function isVisibleCopilotPart(part: ICopilotUIMessage["parts"][number]): boolean {
  if (part.type === CopilotPartEnum.TEXT) return hasVisibleText(part.text);
  if (part.type === CopilotPartEnum.FILE) return true;
  // No `data` at all means no props to bind, so the block renders nothing: not visible content.
  if (VISIBLE_DATA_PART_TYPES.has(part.type)) return "data" in part && isVisibleDataPart(part.type, part.data);
  if (!isToolUIPart(part)) return false;

  const toolName = getToolName(part) as CopilotToolEnum;
  if (VISIBLE_ACTION_TOOLS.has(toolName)) return true;

  return isFinishedTrainingReview(part);
}

/**
 * This answer hands over to the training form: a `reviewTraining` call is running, or has landed.
 * A call that failed is not one of them — the assistant's own words are then all the admin gets.
 */
export function opensTrainingForm(message: ICopilotUIMessage): boolean {
  return message.parts.some(
    (part) =>
      isToolUIPart(part) &&
      getToolName(part) === CopilotToolEnum.REVIEW_TRAINING &&
      part.state !== CopilotToolStateEnum.OUTPUT_ERROR,
  );
}

/** A finished `reviewTraining`: `preliminary` output is partial, so it is not a review yet. */
export function isFinishedTrainingReview(part: ICopilotUIMessage["parts"][number]): boolean {
  if (!isToolUIPart(part) || getToolName(part) !== CopilotToolEnum.REVIEW_TRAINING) return false;

  return part.state === CopilotToolStateEnum.OUTPUT_AVAILABLE && !part.preliminary;
}

/* Reads an AI SDK message. The parts themselves are rendered by `CopilotBlocks`. */

export function textOf(message: ICopilotUIMessage): string {
  return message.parts
    .filter((part) => part.type === CopilotPartEnum.TEXT)
    .map((part) => ("text" in part ? part.text : ""))
    .join("");
}

export function fileNamesOf(message: ICopilotUIMessage): string[] {
  return message.parts
    .filter((part) => part.type === CopilotPartEnum.FILE)
    .map((part) => ("filename" in part && part.filename ? part.filename : "document"));
}

/** A message made only of an attachment already carries its own card: no bubble around it. */
export function isBareOf(message: ICopilotUIMessage): boolean {
  return !!fileNamesOf(message).length && !textOf(message);
}

/**
 * Exactly what `CopilotBlocks` paints, in stream order. The rendering and the emptiness checks read
 * this one list: a part that paints nothing can then never keep an empty bubble — and its thumbs —
 * alive.
 */
export function visiblePartsOf(message: ICopilotUIMessage): ICopilotUIMessage["parts"] {
  const visibleParts = message.parts.filter(isVisibleCopilotPart);
  const attendanceError = visibleParts.find(
    (part) =>
      part.type === ERROR_PART_TYPE && normalizeCopilotError(part).code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION,
  );
  if (attendanceError) {
    const explanation = normalizeCopilotError(attendanceError).message;
    const hasExplanation = !!explanation && textOf(message).includes(explanation.trim());

    // Preserve guidance around the refusal. The structured error is only a fallback until
    // the streamed text contains the complete explanation.
    return visibleParts.filter(
      (part) =>
        part.type === RESOURCES_PART_TYPE ||
        part.type === CopilotPartEnum.TEXT ||
        (!hasExplanation && part === attendanceError),
    );
  }
  /*
   * A review is checked in the form, not in the chat: the recap lists the very fields the CTA opens
   * — of a program, that is the whole program — and the prose around it only says to go and check
   * them there. Both go, as the assistant's text already does beside a resource card. Dropped
   * before the steps are weighed, so an answer ending on the CTA still shows how it got there.
   *
   * Read off the call and not its output: the backend streams the recap while the tool is still
   * running, so waiting for the output painted it for that interval and then took it away — the
   * steps going down and coming back up around it.
   */
  const keptParts = opensTrainingForm(message)
    ? visibleParts.filter((part) => part.type !== RECAP_PART_TYPE && part.type !== CopilotPartEnum.TEXT)
    : visibleParts;
  // Once the recap has landed — or a terminal error — the steps have served their purpose.
  const supersedesAnalysis = keptParts.some((part) => part.type === RECAP_PART_TYPE || part.type === ERROR_PART_TYPE);
  const paintedParts = supersedesAnalysis ? keptParts.filter((part) => part.type !== ANALYSIS_PART_TYPE) : keptParts;
  const hasResources = paintedParts.some((part) => part.type === RESOURCES_PART_TYPE);
  const answerParts = hasResources ? paintedParts.filter((part) => part.type !== CopilotPartEnum.TEXT) : paintedParts;

  // The CTA closes the answer: the tool output lands before the steps it summarizes have finished.
  const reviewPart = visibleParts.find(isFinishedTrainingReview);

  return reviewPart ? [...answerParts.filter((part) => part !== reviewPart), reviewPart] : answerParts;
}

export function hasVisibleContentOf(message: ICopilotUIMessage): boolean {
  return !!visiblePartsOf(message).length;
}

export function shouldRenderMessageOf(message: ICopilotUIMessage, isWaitingForContent: boolean): boolean {
  return message.role !== CopilotRoleEnum.ASSISTANT || isWaitingForContent || hasVisibleContentOf(message);
}
